import { z } from 'zod/v4'
import {
  DataType,
  FieldRequirement,
  RelationshipCardinality,
} from 'skedyul-db/enums'
import {
  BlockSchema,
  CreateBlockSchema,
} from '../features/blocks/resources/types'
import { QuerySchema } from '../features/queries/resources/types'
import {
  VisibilitySchema,
  PermissionsSchema,
  FieldAppearanceSchema,
} from '../../fields/resources/types'

// Input props for getting a page
export const GetPageProps = z.object({
  path: z.string(),
  parentPath: z.string().optional(),
  page: z.number().optional(),
  limit: z.number().optional(),
})

export type GetPageProps = z.infer<typeof GetPageProps>

// Breadcrumb schema
export const BreadcrumbSchema = z.object({
  label: z.string(),
  href: z.string(),
  type: z.enum(['LIST', 'INSTANCE']),
  modelId: z.string(),
})

export type Breadcrumb = z.infer<typeof BreadcrumbSchema>

// Parent page schema
export const ParentPageSchema = z.object({
  title: z.string(),
  icon: z.string().optional(),
  path: z.string(),
  type: z.enum(['LIST', 'INSTANCE']),
})

export type ParentPage = z.infer<typeof ParentPageSchema>

export const PageModelSaveInteractionFieldSchema = z.object({
  handle: z.string(),
  label: z.string(),
  type: z.string().optional(),
  options: z
    .array(
      z.object({
        value: z.string(),
        label: z.string(),
      }),
    )
    .optional(),
})

export const PageModelSaveInteractionSchema = z.object({
  handle: z.string(),
  appHandle: z.string(),
  when: z.object({
    onCreate: z.boolean().optional(),
    onUpdate: z.boolean().optional(),
    fieldsChanged: z.array(z.string()).optional(),
    relationshipsChanged: z.array(z.string()).optional(),
  }),
  dialog: z.object({
    title: z.string(),
    message: z.string(),
    severity: z.enum(['warning', 'info']).optional(),
    fields: z.array(z.string()),
    defaults: z.record(z.string(), z.string()).optional(),
  }),
  fields: z.array(PageModelSaveInteractionFieldSchema),
})

// Model schema
export const PageModelSchema = z.object({
  id: z.string(),
  handle: z.string(),
  name: z.string(),
  namePlural: z.string(),
  handlePlural: z.string(),
  labelTemplate: z.string().optional(),
  imageEnabled: z.boolean().optional(),
  imageSeedTemplate: z.string().nullable().optional(),
  appHandle: z.string().optional(),
  saveInteractions: z.array(PageModelSaveInteractionSchema).optional(),
})

export type PageModel = z.infer<typeof PageModelSchema>

// Field schema
export const FieldSchema = z.object({
  id: z.string(),
  handle: z.string(),
  label: z.string(),
  /** Used where the field's group already gives the context, e.g. a channel. */
  shortLabel: z.string().optional(),
  section: z
    .object({
      handle: z.string(),
      name: z.string(),
      icon: z.string().nullable().optional(),
      iconUrl: z.string().nullable().optional(),
      details: z.array(z.string()).optional(),
      defaultOpen: z.boolean().optional(),
    })
    .nullable()
    .optional(),
  /** Channels using this field as their address. */
  channels: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        icon: z.string().nullable().optional(),
        iconUrl: z.string().nullable().optional(),
        details: z.array(z.string()).optional(),
      }),
    )
    .optional(),
  description: z.string().optional(),
  appearance: FieldAppearanceSchema,
  modelId: z.string(),
  system: z.boolean().default(false),
  isUnique: z.boolean(),
  requirement: z.nativeEnum(FieldRequirement),
  visibility: VisibilitySchema.optional().nullable(),
  permissions: PermissionsSchema.optional().nullable(),
  // The field's data type (STRING, NUMBER, BOOLEAN, etc.)
  dataType: z.nativeEnum(DataType).optional(),
  definition: z
    .object({
      type: z.string(),
      limitChoices: z.number().nullable(),
      handle: z.string(),
      appearance: FieldAppearanceSchema,
      options: z.array(
        z.object({
          id: z.string(),
          value: z.string(),
          label: z.string(),
          color: z.string().nullable().optional(),
          icon: z.string().nullable().optional(),
        }),
      ),
      normalizations: z
        .array(
          z.object({
            handle: z.string(),
          }),
        )
        .optional()
        .default([]),
    })
    .nullable(),
  relationshipLink: z
    .object({
      id: z.string(),
      cardinality: z.nativeEnum(RelationshipCardinality),
      targetModelId: z.string(),
      sourceModelId: z.string(),
    })
    .optional()
    .nullable(),
  // Client-side only fields
  error: z
    .object({
      code: z.enum(['REQUIRED', 'REGEX', 'UNIQUE']),
      message: z.string(),
    })
    .optional(),
  isLoading: z.boolean().default(false).optional(),
})

export type Field = z.infer<typeof FieldSchema>

// Contacts associated to the instance (and its ONE_TO_ONE / MANY_TO_ONE parents)
export const ContactSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
})

export type ContactSummary = z.infer<typeof ContactSummarySchema>

// Communication channels linked via ContactAssociationLink
export const CommunicationChannelSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  // Channel handle from executable.config (skedyul.config.ts)
  handle: z.string(),
  identifierValue: z.string(),
  appInstallationId: z.string(),
  // Resolved from executable config capabilities.messaging.send_batch
  sendBatch: z.string().optional(),
  // Contact association links for this channel
  contactAssociationLinks: z
    .array(
      z.object({
        id: z.string(),
        modelId: z.string(),
      }),
    )
    .optional(),
})

export type CommunicationChannelSummary = z.infer<
  typeof CommunicationChannelSummarySchema
>

// Main page response schema
export const GetPageResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  avatar: z
    .object({
      seed: z.string().nullable(),
    })
    .optional(),
  modelId: z.string(),
  icon: z.string().optional().nullable(),
  parentPageId: z.string().optional().nullable(),
  path: z.string(),
  type: z.enum(['LIST', 'INSTANCE']),
  memberId: z.string().nullable().optional(),
  isDefault: z.boolean().optional(),
  visibility: z.enum(['team', 'private']).optional(),
  baseQuery: QuerySchema.optional().nullable(),
  parentPage: ParentPageSchema.optional().nullable(),
  instancePagePath: z.string().optional(),
  // Clean structure with separate arrays
  models: z.array(PageModelSchema),
  blocks: z.array(BlockSchema),
  fields: z.array(FieldSchema),
  params: z.record(z.string(), z.string()).optional(),
  breadcrumbs: z.array(BreadcrumbSchema),
  consolidatedData: z.record(z.string(), z.unknown()).optional(),
  communicationChannels: z.array(CommunicationChannelSummarySchema).default([]),
  setupIssues: z
    .array(
      z.object({
        code: z.enum(['MODEL_NOT_FOUND', 'FIELD_MAPPING']),
        modelHandle: z.string().optional(),
        message: z.string().optional(),
      }),
    )
    .optional(),
})

export const CreatePageSchema = z.object({
  title: z.string(),
  icon: z.string().optional(),
  path: z.string(),
  type: z.enum(['LIST', 'INSTANCE']),
  parentPageId: z.string().optional(),
  modelId: z.string(),
  memberId: z.string().nullable().optional(),
  isDefault: z.boolean().optional(),
  defaultViewType: z.enum(['spreadsheet', 'kanban', 'calendar']).optional(),
  baseQuery: QuerySchema.optional(),
  blocks: z.array(CreateBlockSchema),
})

export const CreateListViewSchema = z.object({
  modelId: z.string(),
  title: z.string(),
  path: z.string(),
  icon: z.string().optional(),
  memberId: z.string().nullable().optional(),
  isDefault: z.boolean().optional(),
  defaultViewType: z.enum(['spreadsheet', 'kanban', 'calendar']).optional(),
  baseQuery: QuerySchema.optional(),
})

export const UpdateListViewSchema = z.object({
  pageId: z.string(),
  title: z.string().optional(),
  path: z.string().optional(),
  icon: z.string().optional().nullable(),
  baseQuery: QuerySchema.optional().nullable(),
  isDefault: z.boolean().optional(),
})

export const DeleteListViewSchema = z.object({
  pageId: z.string(),
})

export const ListModelViewsSchema = z.object({
  modelId: z.string(),
})

export const ModelViewSchema = z.object({
  id: z.string(),
  pageType: z.enum(['LIST', 'INSTANCE']),
  title: z.string(),
  path: z.string(),
  icon: z.string().nullable(),
  isDefault: z.boolean(),
  memberId: z.string().nullable(),
  visibility: z.enum(['team', 'private']),
  baseQuery: QuerySchema.optional().nullable(),
})

export type ModelView = z.infer<typeof ModelViewSchema>

export const SavePageLayoutSchema = z.object({
  pageId: z.string(),
  blockId: z.string(),
  config: z.unknown(),
  baseQuery: QuerySchema.optional().nullable(),
})

export const CreatePageFromLayoutSchema = z.object({
  sourcePageId: z.string(),
  blockId: z.string(),
  config: z.unknown(),
  baseQuery: QuerySchema.optional().nullable(),
  title: z.string(),
  path: z.string().optional(),
})

export const ExposePageToTeamSchema = z.object({
  pageId: z.string(),
  path: z.string().optional(),
})

export const SearchAccessiblePagesSchema = z.object({
  query: z.string().optional(),
  limit: z.number().optional(),
})

export const UpdatePageSchema = CreatePageSchema.extend({
  id: z.string(),
})

export type CreatePageProps = z.infer<typeof CreatePageSchema>
export type UpdatePageProps = z.infer<typeof UpdatePageSchema>
export type GetPage = z.infer<typeof GetPageResponseSchema>
export type CreateListViewInput = z.infer<typeof CreateListViewSchema>
export type UpdateListViewInput = z.infer<typeof UpdateListViewSchema>
export type SavePageLayoutInput = z.infer<typeof SavePageLayoutSchema>
export type CreatePageFromLayoutInput = z.infer<
  typeof CreatePageFromLayoutSchema
>

export const UpdatePageTitleSchema = z.object({
  pageId: z.string(),
  title: z.string().min(1),
})

export type UpdatePageTitleInput = z.infer<typeof UpdatePageTitleSchema>

export const ChangeListPageModelSchema = z.object({
  pageId: z.string(),
  modelId: z.string(),
})

export type ChangeListPageModelInput = z.infer<typeof ChangeListPageModelSchema>

// Re-export filter types from centralized location
export {
  StructuredExpressionSchema,
  SortSchema,
  SortItemSchema,
} from '../../../resources/filterTypes'
